import React, { useEffect, useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import TopNav from "../../components/navigation/TopNav";
import { playSound } from "../../components/helpers/sound";

import StandardScreen, { ScreenContent } from "../../components/layout/StandardScreen";
import Stack from "../../components/layout/Stack";
import Card from "../../components/ui/Card";
import Button from "../../components/ui/Button";
import FormField from "../../components/forms/FormField";
import SelectInput from "../../components/forms/SelectInput";
import StatusMessage from "../../components/ui/StatusMessage";

const narrowShellStyle = {
  width: "100%",
  maxWidth: "720px",
  margin: "0 auto",
};

export default function PayerPayForTrip() {
  const { t } = useTranslation();

  const [availableWallets, setAvailableWallets] = useState([]);
  const [vendorWallets, setVendorWallets] = useState([]);
  const [selectedVendorWalletId, setSelectedVendorWalletId] = useState("");
  const [message, setMessage] = useState("");
  const [verdict, setVerdict] = useState("");
  const [lastAction, setLastAction] = useState("");

  useEffect(() => {
    axios.defaults.headers.common["Authorization"] = `Bearer ${localStorage.getItem("token")}`;
    fetchAvailableWallets();
    fetchVendorWallets();
  }, []);

  const fetchAvailableWallets = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/payer/available-wallets`);
      setAvailableWallets(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("❌ Failed to fetch payer wallets", err);
      setAvailableWallets([]);
    }
  };

  const fetchVendorWallets = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/payer/refundable-wallets`);
      setVendorWallets(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("❌ Failed to fetch vendor route money-box wallets", err);
      setVendorWallets([]);
    }
  };

  const extractValueFromMessage = (backendMessage, key) => {
    if (!backendMessage) return "";

    const match = backendMessage.match(new RegExp(`${key}=([^\\s|]+)`));
    return match?.[1] ?? "";
  };

  const getRouteLabel = (payerWallet, vendorWallet, backendMessage = "") => {
    return (
      payerWallet?.route_name ||
      payerWallet?.walletroute ||
      vendorWallet?.route_name ||
      vendorWallet?.walletroute ||
      extractValueFromMessage(backendMessage, "route") ||
      ""
    );
  };

  const buildSuccessMessage = (resultStatus, payerWallet, vendorWallet, data) => {
    const backendMessage = data?.message || "";

    const route = getRouteLabel(payerWallet, vendorWallet, backendMessage);

    const ticketBalanceAfter =
      data?.from_ticket_balance_after ??
      extractValueFromMessage(backendMessage, "from_ticket_balance_after") ??
      "";

    return [
      `${t("payer_pay_for_trip.messages.approved.result.label")}: ${t(`payer_pay_for_trip.messages.approved.result.${resultStatus}`) || resultStatus}`,
      `${t("payer_pay_for_trip.messages.approved.route.label")}: ${route}`,
      `${t("payer_pay_for_trip.messages.approved.ticket_balance_after.label")}: ${ticketBalanceAfter}`,
    ].join(" | ");
  };

  const buildCancelMessage = (payerWallet, vendorWallet, data) => {
    const backendMessage = data?.message || "";

    const resultStatus =
      data?.verdict ||
      extractValueFromMessage(backendMessage, "result_status") ||
      "cancelled";

    const route = getRouteLabel(payerWallet, vendorWallet, backendMessage);

    const ticketBalanceBefore =
      data?.from_ticket_balance_before ??
      extractValueFromMessage(backendMessage, "from_ticket_balance_before") ??
      extractValueFromMessage(backendMessage, "ticket_balance_before") ??
      payerWallet?.ticket_balance ??
      "";

    const ticketBalanceAfter =
      data?.from_ticket_balance_after ??
      extractValueFromMessage(backendMessage, "from_ticket_balance_after") ??
      "";

    const reasonKey = backendMessage.includes("Not enough ticket balance")
      ? "Not enough ticket balance"
      : "cancelled";

    return [
      `${t("payer_pay_for_trip.messages.cancelled.result.label")}: ${t(`payer_pay_for_trip.messages.cancelled.result.${resultStatus}`) || resultStatus}`,
      `${t("payer_pay_for_trip.messages.cancelled.reason.label")}: ${t(`payer_pay_for_trip.messages.cancelled.reason.${reasonKey}`) || reasonKey}`,
      `${t("payer_pay_for_trip.messages.cancelled.route.label")}: ${route}`,
      `${t("payer_pay_for_trip.messages.cancelled.ticket_balance_before.label")}: ${ticketBalanceBefore}`,
      `${t("payer_pay_for_trip.messages.cancelled.ticket_balance_after.label")}: ${ticketBalanceAfter}`,
    ].join(" | ");
  };

  const handleManualPay = async () => {
    const vendorWallet = vendorWallets.find(
      (v) => Number(v.wallet_id) === Number(selectedVendorWalletId)
    );

    if (!vendorWallet) {
      setMessage(
        `${t("payer_pay_for_trip.messages.no_vendor_wallet.title")} | ` +
          `${t("payer_pay_for_trip.messages.no_vendor_wallet.label")}: ` +
          `${t("payer_pay_for_trip.messages.no_vendor_wallet.text")}`
      );
      setVerdict("cancelled");
      playSound("error");
      return;
    }

    const payerWallet = availableWallets.find(
      (w) => Number(w.route_id) === Number(vendorWallet.route_id)
    );

    if (!payerWallet) {
      setMessage(
        `${t("payer_pay_for_trip.messages.no_matching_payer_wallet.title")} | ` +
          `${t("payer_pay_for_trip.messages.no_matching_payer_wallet.label")}: ` +
          `${t("payer_pay_for_trip.messages.no_matching_payer_wallet.text")}`
      );
      setVerdict("cancelled");
      playSound("error");
      return;
    }

    try {
      const res = await axios.post(`${process.env.REACT_APP_API_URL}/payer/pay-for-trip`, {
        from_wallet_id: payerWallet.wallet_id,
        delta_tickets_from_wallet: -1,
        delta_money_from_wallet: 0,
        to_wallet_id: vendorWallet.wallet_id,
        delta_tickets_to_wallet: 1,
        delta_money_to_wallet: 0,
        header: "pay-for-trip",
      });

      const resultStatus = String(res.data?.verdict || "").toLowerCase();
      const isSuccess = resultStatus === "approved" || resultStatus === "success";

      if (isSuccess) {
        setMessage(buildSuccessMessage(resultStatus, payerWallet, vendorWallet, res.data));
        await fetchAvailableWallets();
        await fetchVendorWallets();
      } else {
        setMessage(buildCancelMessage(payerWallet, vendorWallet, res.data));
      }

      setVerdict(isSuccess ? "approved" : "cancelled");
      playSound(isSuccess ? "success" : "error");
    } catch (err) {
      const errData = err.response?.data || {};
      const errMsg =
        errData?.message ||
        errData?.detail ||
        t("payer_pay_for_trip.messages.technical_failure.text");

      setMessage(
        buildCancelMessage(payerWallet, vendorWallet, {
          verdict: "cancelled",
          message: errMsg,
        })
      );

      setVerdict("cancelled");
      playSound("error");
    } finally {
      setSelectedVendorWalletId("");
    }
  };

  const messageVariant =
    verdict === "approved" ? "success" : message ? "error" : "info";

  return (
    <StandardScreen
      topNav={<TopNav title={t("payer_pay_for_trip.title")} mode="main" />}
    >
      <ScreenContent>
        <div style={narrowShellStyle}>
          <Stack gap="4">
            <Card title={t("payer_pay_for_trip.title")}>
              <Stack gap="3">
                <FormField label={t("payer_pay_for_trip.fields.wallet.label")}>
                  <SelectInput
                    value={selectedVendorWalletId}
                    onChange={(e) => {
                      setSelectedVendorWalletId(e.target.value);
                      setLastAction("");
                      setMessage("");
                      setVerdict("");
                    }}
                  >
                    <option value="">
                      {t("payer_pay_for_trip.fields.wallet.placeholder")}
                    </option>

                    {vendorWallets.map((wallet) => (
                      <option key={wallet.wallet_id} value={wallet.wallet_id}>
                        {[

                            wallet.vendor_name,
                            wallet.route_name,
                            wallet.start_point && wallet.end_point
                              ? `${wallet.start_point} → ${wallet.end_point}`
                              : null,


                            (() => {
                              const payerWallet = availableWallets.find(
                                (w) => Number(w.route_id) === Number(wallet.route_id)
                              );

                              return payerWallet
                                ? `${t("payer_pay_for_trip.dropdown_item.ticket_balance_before")}: ${payerWallet.ticket_balance ?? 0}`
                                : null;
                            })(),

                        ]
                          .filter(Boolean)
                          .join(" | ")}
                      </option>
                    ))}
                  </SelectInput>
                </FormField>

                <Button
                  onClick={() => {
                    setLastAction("pay");
                    handleManualPay();
                  }}
                  disabled={
                    !selectedVendorWalletId ||
                    (lastAction === "pay" && message !== "")
                  }
                  fullWidth
                >
                  {lastAction === "pay"
                    ? verdict === "approved"
                      ? t("payer_pay_for_trip.actions.success")
                      : verdict === "cancelled"
                      ? t("payer_pay_for_trip.actions.failure")
                      : t("payer_pay_for_trip.actions.submit")
                    : t("payer_pay_for_trip.actions.submit")}
                </Button>

                <StatusMessage variant={messageVariant} message={message} />
              </Stack>
            </Card>
          </Stack>
        </div>
      </ScreenContent>
    </StandardScreen>
  );
}